import React, { useState, useEffect, useRef } from "react";
import "./BotAvatar.css";
import avatarImg from "../images/animar.png";

const AVATAR_WIDTH = 68;
const AVATAR_HEIGHT = 96;
const AVATAR_PADDING = 8;
const CLOUD_WIDTH = 300;
const CLOUD_ESTIMATED_HEIGHT = 48;
const CLOUD_GAP = 8;

const BotAvatar = ({ children }) => {
  const [showChat, setShowChat] = useState(false);
  const [showCloud, setShowCloud] = useState(true);
  const [displayedText, setDisplayedText] = useState("");
  const intervalRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const chatRef = useRef(null);

  // DRAG position state
  const [position, setPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('botAvatarPos');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Number.isFinite(parsed.x) && Number.isFinite(parsed.y)) {
            return parsed;
          }
        }
      } catch (_) {}
      // Posición por defecto: esquina inferior derecha visible
      return { 
        x: Math.max(AVATAR_PADDING, window.innerWidth - AVATAR_WIDTH - AVATAR_PADDING),
        y: Math.max(AVATAR_PADDING, window.innerHeight - AVATAR_HEIGHT - AVATAR_PADDING)
      };
    }
    return { x: 20, y: 20 };
  });
  const [viewport, setViewport] = useState(() => {
    if (typeof window !== 'undefined') {
      return { width: window.innerWidth, height: window.innerHeight };
    }
    return { width: 1024, height: 768 };
  });
  const dragRef = useRef(null);
  const draggingRef = useRef(false);
  const startPointerRef = useRef({ x: 0, y: 0 });
  const startPosRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);

  const fullMessage = "¡Hola! ¿En que te puedo ayudar?";
  const typingDuration = 6000;
  const visibleDuration = 15000;

  // Typing effect & cloud visibility
  useEffect(() => {
    if (showCloud && !showChat) {
      setDisplayedText("");
      const charInterval = Math.max(1, Math.floor(typingDuration / fullMessage.length));
      let i = 0;
      typingIntervalRef.current = setInterval(() => {
        i++;
        setDisplayedText(fullMessage.slice(0, i));
        if (i >= fullMessage.length) clearInterval(typingIntervalRef.current);
      }, charInterval);
      hideTimeoutRef.current = setTimeout(() => setShowCloud(false), visibleDuration);
      return () => {
        clearInterval(typingIntervalRef.current);
        clearTimeout(hideTimeoutRef.current);
      };
    }
  }, [showCloud, showChat]);

  // Cloud reappears periodically if chat closed
  useEffect(() => {
    if (!showChat) {
      intervalRef.current = setInterval(() => setShowCloud(true), 10000);
    }
    return () => clearInterval(intervalRef.current);
  }, [showChat]);

  // Hide cloud when chat opens
  useEffect(() => { if (showChat) setShowCloud(false); }, [showChat]);

  // Outside click to close chat
  useEffect(() => {
    if (!showChat) return;
    const handler = (e) => { if (chatRef.current && !chatRef.current.contains(e.target)) setShowChat(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showChat]);

  // Persist position
  useEffect(() => { try { localStorage.setItem('botAvatarPos', JSON.stringify(position)); } catch (_) {} }, [position]);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  // Ajustar posición cuando cambia el tamaño de ventana para mantener visible
  // Clamp helper
  const clampPosition = (x, y, width = viewport.width, height = viewport.height) => {
    const maxX = Math.max(AVATAR_PADDING, width - AVATAR_WIDTH - AVATAR_PADDING);
    const maxY = Math.max(AVATAR_PADDING, height - AVATAR_HEIGHT - AVATAR_PADDING);
    return {
      x: Math.min(Math.max(x, AVATAR_PADDING), maxX),
      y: Math.min(Math.max(y, AVATAR_PADDING), maxY)
    };
  };

  const visiblePosition = clampPosition(position.x, position.y);
  const avatarCenterX = visiblePosition.x + (AVATAR_WIDTH / 2);
  const availableCloudWidth = Math.max(120, viewport.width - 24);
  const cloudWidth = Math.min(CLOUD_WIDTH, availableCloudWidth);
  const cloudLeft = Math.min(
    Math.max(avatarCenterX - (cloudWidth / 2), 12),
    Math.max(12, viewport.width - cloudWidth - 12)
  );
  const canPlaceCloudAbove = visiblePosition.y >= CLOUD_ESTIMATED_HEIGHT + CLOUD_GAP + 12;
  const cloudTop = canPlaceCloudAbove
    ? Math.max(12, visiblePosition.y - CLOUD_ESTIMATED_HEIGHT - CLOUD_GAP)
    : Math.min(
        Math.max(12, viewport.height - CLOUD_ESTIMATED_HEIGHT - 12),
        visiblePosition.y + AVATAR_HEIGHT + CLOUD_GAP
      );
  const cloudTailLeft = Math.min(Math.max(avatarCenterX - cloudLeft, 24), cloudWidth - 24);

  const onPointerDown = (e) => {
    if (showChat) return; // no mover mientras conversan
    draggingRef.current = true;
    const { clientX, clientY } = e;
    startPointerRef.current = { x: clientX, y: clientY };
    startPosRef.current = { ...visiblePosition };
    movedRef.current = false;
    dragRef.current?.classList.add('dragging');
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    const dx = e.clientX - startPointerRef.current.x;
    const dy = e.clientY - startPointerRef.current.y;
    if (!movedRef.current) {
      const distance = Math.sqrt(dx*dx + dy*dy);
      if (distance > 6) movedRef.current = true; // umbral de 6px para considerar drag real
    }
    setPosition(clampPosition(startPosRef.current.x + dx, startPosRef.current.y + dy));
  };

  const onPointerUp = () => {
    draggingRef.current = false;
    dragRef.current?.classList.remove('dragging');
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    // Si no se movió lo suficiente, tratarlo como click
    if (!movedRef.current && !showChat) {
      setShowChat(true);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowChat(prev => !prev);
    }
  };

  return (
    <div className="bot-avatar-container" style={{ left: visiblePosition.x, top: visiblePosition.y }}>
      <div style={{ position: 'relative', width: '68px', height: '96px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {showCloud && !showChat && (
          <div
            className={`bot-cloud-msg ${canPlaceCloudAbove ? '' : 'bot-cloud-msg-below'}`}
            style={{
              left: cloudLeft,
              top: cloudTop,
              width: cloudWidth,
              '--bot-cloud-tail-left': `${cloudTailLeft}px`
            }}
          >
            {displayedText}
          </div>
        )}
        <img
          ref={dragRef}
          src={avatarImg}
          alt="Bot" aria-label="Abrir chat de ayuda"
          className="bot-float-avatar bot-draggable"
          onPointerDown={onPointerDown}
          tabIndex={0}
          role="button"
          onKeyDown={onKeyDown}
        />
      </div>
      {showChat && (
        <div className="bot-chat-popup" ref={chatRef}>
          <button className="bot-close-btn" onClick={() => setShowChat(false)}>×</button>
          {children}
        </div>
      )}
    </div>
  );
};

export default BotAvatar;
